import React, { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import "./admin.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Menu from "./screens/Menu";
import Instructions from "./screens/Instructions";
import LevelSelect from "./screens/LevelSelect";
import EndScreen from "./screens/EndScreen";
import Leaderboard from "./screens/Leaderboard";
import VipScreen from "./screens/VipScreen";
import VipSuccess from "./screens/VipSuccess";
import CosmeticsScreen from "./screens/CosmeticsScreen";
import TalentsScreen from "./screens/TalentsScreen";
import AchievementsScreen from "./screens/AchievementsScreen";
import AdminScreen from "./screens/AdminScreen";
import Game from "./game/Game";
import GameErrorBoundary from "./components/GameErrorBoundary";
import ArcadeHome from "./arcade/ArcadeHome";
import ComingSoon from "./arcade/ComingSoon";
import SolitaireScreen from "./games/solitaire/SolitaireScreen";
import MergeScreen from "./games/merge/MergeScreen";
import CrushScreen from "./games/crush/CrushScreen";
import TycoonScreen from "./games/tycoon/TycoonScreen";
import PrivacyPage from "./legal/PrivacyPage";
import TermsPage from "./legal/TermsPage";
import AboutPage from "./legal/AboutPage";
import ContactPage from "./legal/ContactPage";
import { loadSave, saveGame, resetSave } from "./game/storage";
import { getLevel, getScaledLevel, LEVELS } from "./game/levels";
import { DEFAULT_RUN_POWERS, RUN_POWERS } from "./game/upgrades";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster, toast } from "sonner";
import useSEO from "./seo/useSEO";
import { SEO_GAMES } from "./seo/seoData";

function isVipSuccessRoute() {
  return typeof window !== "undefined" && window.location.pathname.startsWith("/vip/success");
}

// Soft VIP / Founder perk (Feb 2026):
// When a VIP/Founder starts a fresh run, sample 1 (VIP) or 2 (Founder) random
// powers they had at the end of their previous run and grant them at Lv 1.
// This makes the tiers feel valuable without breaking the roguelike loop.
function buildVipCarryOver(lastPowers, user) {
  if (!user || !lastPowers) return { powers: { ...DEFAULT_RUN_POWERS }, granted: [] };
  const slots = user.founder ? 2 : (user.is_vip ? 1 : 0);
  if (slots <= 0) return { powers: { ...DEFAULT_RUN_POWERS }, granted: [] };
  const owned = Object.keys(lastPowers).filter((id) => (lastPowers[id] || 0) > 0 && RUN_POWERS[id]);
  if (owned.length === 0) return { powers: { ...DEFAULT_RUN_POWERS }, granted: [] };
  const shuffled = [...owned].sort(() => Math.random() - 0.5).slice(0, slots);
  const next = { ...DEFAULT_RUN_POWERS };
  for (const id of shuffled) next[id] = 1;
  return { powers: next, granted: shuffled };
}

function AppInner() {
  useSEO(SEO_GAMES["zombie-collector-3d"]);
  const { user } = useAuth();
  // If we land on the Stripe success route, jump straight to that screen.
  const [screen, setScreen] = useState(() =>
    isVipSuccessRoute() ? "vip-success" : "menu"
  );
  const [save, setSave] = useState(() => loadSave());
  const [currentLevelId, setCurrentLevelId] = useState(1);
  const [endStats, setEndStats] = useState(null);
  const [endType, setEndType] = useState("win"); // 'win' | 'lose' | 'victory'
  const [gameRunId, setGameRunId] = useState(0);
  // Per-run revive counter (Phase 4 — extra_revive talent). Reset on every
  // new run/quit/death so revives don't accidentally carry over.
  const revivesLeftRef = useRef(0);
  const initRevives = useCallback((talentLvl) => {
    revivesLeftRef.current = Math.min(3, talentLvl || 0);
  }, []);
  // In-run upgrades — kept here (not inside Game) so they survive level
  // transitions inside one continuous run. Reset when starting a new run
  // from menu/level-select, or when quitting back to menu.
  const [runPowers, setRunPowers] = useState(() => ({ ...DEFAULT_RUN_POWERS }));
  // Snapshot of the LAST completed/died run's powers. Used to seed VIP/Founder
  // carry-over on the next fresh run. Lives in memory only (resets on reload).
  const lastRunPowersRef = useRef(null);
  // Whether the upcoming Game mount represents a brand-new run (not a wave
  // transition inside one ongoing run). With VIP carry-over we can no longer
  // infer this from `runPowers === all-zeros` — Game.jsx reads this prop to
  // decide whether to re-init revives.
  const isFreshRunRef = useRef(true);

  useEffect(() => {
    saveGame(save);
  }, [save]);

  // Global async/loop error catcher. React error boundaries only catch
  // errors thrown during render/commit — they do NOT catch errors thrown
  // inside requestAnimationFrame callbacks (e.g. R3F useFrame loops), which
  // is exactly the category that froze the game during the Companion Ships
  // bug. This listener fills that gap: when an uncaught error fires while
  // the player is on the "playing" screen, we surface a toast and bail
  // back to the menu instead of letting the frozen canvas trap them.
  useEffect(() => {
    if (screen !== "playing") return;
    let firedThisRun = false;
    const handler = (event) => {
      if (firedThisRun) return;
      firedThisRun = true;
      console.error("[App] Caught uncaught error during play:", event?.error || event);
      toast.error("Game hit an error", {
        description: "Your account is safe. Returning to menu.",
        duration: 4000,
      });
      // Snapshot the build before reset for VIP carry-over (same as quit).
      setRunPowers((current) => {
        lastRunPowersRef.current = { ...current };
        return { ...DEFAULT_RUN_POWERS };
      });
      setScreen("menu");
    };
    window.addEventListener("error", handler);
    const rejectHandler = (event) => handler({ error: event.reason, message: String(event.reason) });
    window.addEventListener("unhandledrejection", rejectHandler);
    return () => {
      window.removeEventListener("error", handler);
      window.removeEventListener("unhandledrejection", rejectHandler);
    };
  }, [screen]);

  const go = (s) => setScreen(s);
  const startRun = () => setGameRunId((n) => n + 1);

  // Reset run powers, applying VIP/Founder carry-over when applicable.
  const resetRunPowers = useCallback(() => {
    isFreshRunRef.current = true;
    const { powers, granted } = buildVipCarryOver(lastRunPowersRef.current, user);
    setRunPowers(powers);
    if (granted.length > 0) {
      const names = granted.map((id) => RUN_POWERS[id]?.name || id).join(", ");
      const tier = user?.founder ? "Founder" : "VIP";
      toast.success(`${tier} Carry-Over: ${names}`, {
        description: `Kept from your last run — enjoy the head start!`,
        duration: 4500,
      });
    }
  }, [user]);

  // Play - pick highest unlocked uncompleted level, or first unlocked
  const handlePlay = () => {
    const next =
      save.unlockedLevels
        .map((id) => id)
        .find((id) => !save.completedLevels.includes(id)) ||
      save.unlockedLevels[save.unlockedLevels.length - 1] ||
      1;
    setCurrentLevelId(next);
    resetRunPowers();
    startRun();
    setScreen("playing");
  };

  const handleSelectLevel = (id) => {
    setCurrentLevelId(id);
    resetRunPowers();
    startRun();
    setScreen("playing");
  };

  const handleQuitGame = () => {
    // Snapshot the final build for VIP carry-over even on quit.
    setRunPowers((current) => {
      lastRunPowersRef.current = { ...current };
      return { ...DEFAULT_RUN_POWERS };
    });
    setScreen("menu");
  };

  const handleWin = useCallback(
    ({ score, coinsEarned, zombiesCollected, bossesDefeated, playSeconds }) => {
      const isLast = currentLevelId >= LEVELS.length;
      const currentWave = save.wave || 1;
      const nextWave = isLast ? currentWave + 1 : currentWave;
      const nextLevelInRun = isLast ? 1 : currentLevelId + 1;
      const finishedFirstLoop = isLast && currentWave === 1;
      const level = getScaledLevel(currentLevelId, currentWave);
      setSave((s) => {
        const completed = s.completedLevels.includes(currentLevelId)
          ? s.completedLevels
          : [...s.completedLevels, currentLevelId];
        // In endless mode every world is always unlockable.
        const allUnlocked = LEVELS.map((l) => l.id);
        const high = Math.max(s.highScores[currentLevelId] || 0, score);
        return {
          ...s,
          coins: s.coins + coinsEarned,
          totalScore: Math.max(s.totalScore, score),
          unlockedLevels: allUnlocked,
          completedLevels: completed,
          highScores: { ...s.highScores, [currentLevelId]: high },
          wave: nextWave,
          highestWave: Math.max(s.highestWave || 1, nextWave),
        };
      });
      setEndStats({
        score,
        coinsEarned,
        zombiesCollected,
        level,
        nextLevelInRun,
        nextWave,
        wave: currentWave,
        bossesDefeated: bossesDefeated || 0,
        playSeconds: playSeconds || 0,
        completed: true,
      });
      // Show 'victory' once (when wrapping out of wave 1) — every subsequent
      // wrap is a 'wave' screen so the player can keep going forever.
      setEndType(finishedFirstLoop ? "victory" : isLast ? "wave" : "win");
      setScreen("end");
    },
    [currentLevelId, save.wave]
  );

  const handleLose = useCallback(({ score, coinsEarned, zombiesCollected, bossesDefeated, playSeconds }) => {
    setSave((s) => ({
      ...s,
      coins: s.coins + Math.floor(coinsEarned / 2),
    }));
    // Snapshot the build BEFORE clearing it so EndScreen can show it AND
    // VIP carry-over has something to sample from on the next run.
    setRunPowers((current) => {
      lastRunPowersRef.current = { ...current };
      setEndStats({
        score,
        coinsEarned: Math.floor(coinsEarned / 2),
        zombiesCollected,
        runPowers: { ...current },
        bossesDefeated: bossesDefeated || 0,
        playSeconds: playSeconds || 0,
        completed: false,
      });
      return { ...DEFAULT_RUN_POWERS };
    });
    setEndType("lose");
    setScreen("end");
  }, []);

  const handleRetry = () => {
    // Retry counts as a fresh run — apply VIP carry-over from the just-ended run.
    resetRunPowers();
    startRun();
    setScreen("playing");
  };

  const handleNextLevel = () => {
    isFreshRunRef.current = false;
    setCurrentLevelId((id) => (id >= LEVELS.length ? 1 : id + 1));
    startRun();
    setScreen("playing");
  };

  const handleReset = () => {
    const fresh = resetSave();
    setSave(fresh);
  };

  const level = getScaledLevel(currentLevelId, save.wave || 1);
  // In endless mode, there is no last level — L5 wraps back to L1 of the next wave.
  const isLastLevel = false;

  return (
    <div className="zc-app" data-testid="app-root">
      <Toaster position="top-center" theme="dark" />
      {screen === "menu" && (
        <Menu
          save={save}
          onPlay={handlePlay}
          onInstructions={() => go("instructions")}
          onLevelSelect={() => go("levels")}
          onLeaderboard={() => go("leaderboard")}
          onVip={() => go("vip")}
          onCosmetics={() => go("cosmetics")}
          onTalents={() => go("talents")}
          onAchievements={() => go("achievements")}
          onReset={handleReset}
        />
      )}
      {screen === "instructions" && <Instructions onBack={() => go("menu")} />}
      {screen === "leaderboard" && <Leaderboard onBack={() => go("menu")} />}
      {screen === "vip" && <VipScreen onBack={() => go("menu")} />}
      {screen === "vip-success" && (
        <VipSuccess
          onContinue={() => {
            // Clear the success URL and go back to menu.
            window.history.replaceState(null, "", "/");
            go("menu");
          }}
        />
      )}
      {screen === "cosmetics" && <CosmeticsScreen onBack={() => go("menu")} />}
      {screen === "talents" && <TalentsScreen onBack={() => go("menu")} />}
      {screen === "achievements" && <AchievementsScreen onBack={() => go("menu")} />}
      {screen === "levels" && (
        <LevelSelect
          save={save}
          onBack={() => go("menu")}
          onSelect={handleSelectLevel}
        />
      )}
      {screen === "playing" && (
        <GameErrorBoundary
          key={`eb-${gameRunId}`}
          onReturnToMenu={handleQuitGame}
          onRestart={handleRetry}
        >
          <Game
            key={`game-${gameRunId}`}
            level={level}
            upgrades={save.upgrades}
            runPowers={runPowers}
            setRunPowers={setRunPowers}
            revivesRef={revivesLeftRef}
            initRevives={initRevives}
            isFreshRun={isFreshRunRef.current}
            onWin={handleWin}
            onLose={handleLose}
            onQuit={handleQuitGame}
          />
        </GameErrorBoundary>
      )}
      {screen === "end" && endStats && (
        <EndScreen
          type={endType}
          level={level}
          stats={endStats}
          runPowers={endStats.runPowers || runPowers}
          isLastLevel={isLastLevel}
          onRetry={handleRetry}
          onNext={handleNextLevel}
          onMenu={() => go("menu")}
        />
      )}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Arcade homepage */}
          <Route path="/" element={<ArcadeHome />} />
          <Route path="/arcade" element={<Navigate to="/" replace />} />

          {/* Zombie Collector 3D — the only currently-live game */}
          <Route path="/games/zombie-collector-3d" element={<AppInner />} />

          {/* Stripe checkout returns here. Live inside the ZC3D shell so
              VipSuccess can refresh auth state and the user can continue
              into the game after paying. */}
          <Route path="/vip/success" element={<AppInner />} />

          {/* Coming-soon placeholder games. Each pulls metadata from
              /src/arcade/games.js so their branding stays consistent. */}
          <Route path="/games/zombie-solitaire" element={<SolitaireScreen />} />
          <Route path="/games/zombie-merge" element={<MergeScreen />} />
          <Route path="/games/zombie-crush" element={<CrushScreen />} />
          <Route path="/games/zombie-tycoon" element={<TycoonScreen />} />
          <Route path="/games/zombie-tower-defense" element={<ComingSoon slug="zombie-tower-defense" />} />

          {/* Legal / trust pages — visible to Google AdSense reviewers */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          {/* Generic catch-all for any future /games/:slug we haven't wired yet */}
          <Route path="/games/:slug" element={<ComingSoon />} />

          {/* Admin — backend enforces permissions; this is just the UI mount */}
          <Route path="/admin" element={
            <div className="zc-app" data-testid="app-root">
              <Toaster position="top-center" theme="dark" />
              <AdminScreen />
            </div>
          } />

          {/* Fallback — anything unknown goes back to the arcade home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
